import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { AnimatePresence, motion } from "motion/react";
import { animate } from "motion/mini";
import type { VisualPhase, VisualState } from "./types";
import "./effects.css";

const COLORS = ["#f6c453", "#ef4444", "#3b82f6", "#10b981", "#8b5cf6", "#22d3ee"];

function detectPhase(): VisualPhase {
  if (document.querySelector(".end-stage")) return "ended";
  if (document.querySelector(".wager-stage,.player-wager")) return "wager";
  if (document.querySelector(".player-result,.round-results")) return "results";
  if (document.querySelector(".question-panel,.player-question")) return "question";
  if (document.querySelector(".lobby-layout,.player-lobby")) return "lobby";
  return "setup";
}

function GameEffects() {
  const [visual, setVisual] = useState<VisualState>({
    phase: "setup",
    burst: "none",
    key: 0,
    reducedMotion: matchMedia("(prefers-reduced-motion: reduce)").matches
  });

  useEffect(() => {
    let previous = "setup" as VisualPhase;
    let previousScene = "";
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const phase = detectPhase();
        const burst = phase === "ended" ? "celebration" : "none";
        const scene = [
          phase,
          document.querySelector(".question-title,.player-question h2")?.textContent || "",
          document.querySelectorAll(".player-card").length
        ].join(":");
        if (phase !== previous || burst !== "none") {
          previous = phase;
          setVisual((current) => ({ ...current, phase, burst, key: current.key + 1 }));
        }
        if (scene !== previousScene) {
          previousScene = scene;
          document.querySelectorAll<HTMLElement>(".entrance").forEach((element) => {
            animate(element, { opacity: [0, 1], y: [12, 0] }, { duration: .3, ease: "easeOut" });
          });
          document.querySelectorAll<HTMLElement>(".answer-button,.answer-tile,.player-card").forEach((element, index) => {
            animate(element, { opacity: [0, 1], scale: [.98, 1], y: [7, 0] }, { delay: index * .025, duration: .22 });
          });
        }
      });
    };
    const observer = new MutationObserver(update);
    observer.observe(document.querySelector("#app")!, { childList: true, subtree: true });
    document.documentElement.classList.add("motion-ready");
    update();
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, []);

  useEffect(() => {
    if (visual.reducedMotion || visual.phase !== "ended") return;
    let timeline: ReturnType<(typeof import("gsap"))["gsap"]["timeline"]> | undefined;
    let disposed = false;
    void import("gsap").then(({ gsap }) => {
      if (disposed) return;
      timeline = gsap.timeline();
      timeline.fromTo(".podium-card.place-3", { y: 120, opacity: 0 }, { y: 0, opacity: 1, duration: .55, ease: "back.out(1.7)" })
        .fromTo(".podium-card.place-2", { y: 140, opacity: 0 }, { y: 0, opacity: 1, duration: .6, ease: "back.out(1.7)" }, "-=.3")
        .fromTo(".podium-card.place-1", { y: 180, opacity: 0, scale: .8 }, { y: 0, opacity: 1, scale: 1, duration: .8, ease: "elastic.out(1,.55)" }, "-=.35");
    });
    return () => { disposed = true; timeline?.kill(); };
  }, [visual.phase, visual.key, visual.reducedMotion]);

  return <Effects visual={visual} />;
}

function Effects({ visual }: { visual: VisualState }) {
  if (visual.reducedMotion) return null;
  const activeAtmosphere = ["lobby", "question"].includes(visual.phase);
  return (
    <AnimatePresence mode="sync">
      {activeAtmosphere && (
        <motion.div key={`atmosphere-${visual.phase}`} className="fx-atmosphere" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          {Array.from({ length: visual.phase === "lobby" ? 12 : 6 }, (_, index) => (
            <motion.i
              className="fx-particle"
              key={`${visual.phase}-${index}`}
              style={{ left: `${5 + ((index * 23) % 90)}%`, "--size": `${4 + (index % 4) * 3}px`, "--color": COLORS[index % COLORS.length] } as React.CSSProperties}
              animate={{ y: [20, -900], x: [0, (index % 2 ? 1 : -1) * 35], rotate: [0, 240], opacity: [0, .7, 0] }}
              transition={{ duration: 8 + index % 5, repeat: Infinity, delay: index * .38, ease: "linear" }}
            />
          ))}
        </motion.div>
      )}
      {visual.burst !== "none" && (
        <motion.div key={`burst-${visual.key}`} className="fx-burst" initial={{ opacity: .9, scale: 1 }} animate={{ opacity: 0, scale: 35 }} exit={{ opacity: 0 }} transition={{ duration: .85, ease: "easeOut" }} />
      )}
    </AnimatePresence>
  );
}

createRoot(document.querySelector("#effects-root")!).render(<GameEffects />);
const behaviorScript = document.createElement("script");
behaviorScript.type = "module";
behaviorScript.src = "/app.js";
document.body.append(behaviorScript);
